import React, { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Eye, Edit, Trash2, Star } from "lucide-react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableAction, TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import EmailDetails from "../details/details";
import EmailDelete from "../delete/delete";
import {
  useDeleteEmailSignature,
  useGetAllEmailSignatures,
  useUpdateEmailSignatureStatus,
} from "../../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
interface EmailSignaturelistProps {
  openCreateEmailSignature: boolean;
  setOpenCreateEmailSignature: (value: boolean) => void;
  selectedSignature: any | null;
  setSelectedSignature: (value: any | null) => void;
  isEdit: (signature: any) => void;
}

const EmailSignaturelist: React.FC<EmailSignaturelistProps> = ({
  openCreateEmailSignature,
  setOpenCreateEmailSignature,
  selectedSignature,
  setSelectedSignature,
  isEdit,
}) => {
  const utilityId = useRemoteUtilityId();
  const [searchParams, setSearchParams] = useSearchParams();
  const [getSignatureID, setGetSignatureID] = useState<string | null>(null);
  const [openDetailSignature, setOpenDetailSignature] = useState(false);
  const [openDeleteSignature, setOpenDeleteSignature] = useState(false);


  // Read params from URL
  const searchQuery = decodeURIComponent(searchParams.get("search") || "");
  const currentPage = parseInt(searchParams.get("page") || "1");
  const { data: emailSignatures } = useGetAllEmailSignatures(
    currentPage,
    PAGE_SIZE,
    searchQuery
  );
  const { mutateAsync: updateEmailSignatureStatus } =
    useUpdateEmailSignatureStatus(getSignatureID);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  // Update pagination
  React.useEffect(() => {
    const totalItems = emailSignatures?.count || 0;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    const startItem = (currentPage - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

    setPagination({
      currentPage: currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    });
  }, [emailSignatures, currentPage]);

  const handleSearch = (searchTerm: string) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
    };

    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search;
    }

    newParams.page = "1"; // Reset to first page on search
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      page: page.toString(),
    };
    setSearchParams(newParams);
  };

  const handleSetDefault = (row: any) => {
    const payload = {
      remote_utility_id: utilityId,
      is_default: true,
    };
    updateEmailSignatureStatus(payload);
    setGetSignatureID(row.id);
  };
  const columns: TableColumn<any>[] = [
    {
      key: "name",
      header: "Name",
      sortable: true,
      render: (value: string, row: any) => (
        <div>
          <p className="font-medium">{value}</p>
        </div>
      ),
    },
    {
      key: "preview",
      header: "Preview",
      sortable: true,
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (value: boolean) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            value ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"
          }`}
        >
          {value ? "Default" : "Set as Default"}
        </span>
      ),
    },
    {
      key: "createdBy",
      header: "Created By",
      sortable: true,
    },
  ];

  const actions: TableAction<any>[] = [
    {
      label: "View",
      onClick: (row: any) => {
        setSelectedSignature(row);
        setOpenDetailSignature(true);
      },
      icon: <Eye className="h-4 w-4" />,
    },
    {
      label: "Edit",
      onClick: (row: any) => {
        isEdit(row);
        setOpenCreateEmailSignature(true);
      },
      icon: <Edit className="h-4 w-4" />,
    },
    {
      label: "Set Default",
      onClick: (row: any) => {
        handleSetDefault(row);
      },
      icon: <Star className="h-4 w-4" />,
      hidden: (row: any) => row.isDefault,
    },
    {
      label: "delete",
      onClick: (row: any) => {
        setSelectedSignature(row);
        setOpenDeleteSignature(true);
      },
      icon: <Trash2 className="h-4 w-4" />,
    },
  ];

  return (
    <div className="space-y-6">
      <GenericTable
        data={emailSignatures?.results || []}
        columns={columns}
        rowKey="id"
        selectable={false}
        actions={actions}
        actionsType="dropdown"
        emptyMessage="No email signatures found matching your criteria"
        pagination={pagination}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        searchPlaceholder="Search by signature name..."
      />
      <EmailDetails
        openDetailEmail={openDetailSignature}
        setOpenDetailEmail={setOpenDetailSignature}
        signatureId={selectedSignature?.id}
      />
      <EmailDelete
        openDeleteEmail={openDeleteSignature}
        setOpenDeleteEmail={setOpenDeleteSignature}
        signatureId={selectedSignature?.id}
        name={selectedSignature?.name}
      />
    </div>
  );
};

export default EmailSignaturelist;
